/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  AdminGetCreditNotePdfParams,
  AdminListRefundsParams,
  AdminPayoutRefundBody,
  AdminRefund,
  AdminRefundPage,
  ConflictResponse,
  ForbiddenResponse,
  InternalErrorResponse,
  ListRefundsParams,
  NotFoundResponse,
  PayloadTooLargeResponse,
  QuoteRefundParams,
  ReasonInput,
  Refund,
  RefundPage,
  RefundQuote,
  RequestRefundBody,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getRequestRefundUrl = () => {




  return `/refunds`
}

/**
 * Takes the credit out of the wallet at once. The destination is one
 * `GET /refunds/quote` offered, or a bank account. One open request at
 * a time (409 `refund_pending`).
 * @summary Ask for unused paid credit back
 */
export const requestRefund = async (requestRefundBody: RequestRefundBody, options?: Parameters<typeof apiFetch>[1]): Promise<Refund> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Refund>(getRequestRefundUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(requestRefundBody)
  }
);}





export const getRequestRefundMutationKey = () => ['requestRefund'] as const;

export const getRequestRefundMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestRefund>>, TError,RequestRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestRefund>>, TError,RequestRefundMutationVariables, TContext> => {

const mutationKey = getRequestRefundMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestRefund>>, RequestRefundMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  requestRefund(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestRefundMutationResult = NonNullable<Awaited<ReturnType<typeof requestRefund>>>
    export type RequestRefundMutationBody = RequestRefundBody
    export type RequestRefundMutationError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type RequestRefundMutationVariables = {data: RequestRefundBody}

    /**
 * @summary Ask for unused paid credit back
 */
export const useRequestRefund = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestRefund>>, TError,RequestRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof requestRefund>>,
        TError,
        RequestRefundMutationVariables,
        TContext
      > => {
      return useMutation(getRequestRefundMutationOptions(options), queryClient);
    }
    export const getListRefundsUrl = (params?: ListRefundsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/refunds?${stringifiedParams}` : `/refunds`
}

/**
 * @summary The caller's refund requests, newest first
 */
export const listRefunds = async (params?: ListRefundsParams, options?: Parameters<typeof apiFetch>[1]): Promise<RefundPage> => {

  return apiFetch<RefundPage>(getListRefundsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRefundsInfiniteQueryKey = (params?: ListRefundsParams,) => {
    return [
    'infinite', `/refunds`, ...(params ? [params] : [])
    ] as const;
    }

export const getListRefundsQueryKey = (params?: ListRefundsParams,) => {
    return [
    `/refunds`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRefundsInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof listRefunds>>, ListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData, QueryKey, ListRefundsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRefundsInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRefunds>>, QueryKey, ListRefundsParams['cursor']> = ({ signal, pageParam }) => listRefunds({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData, QueryKey, ListRefundsParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListRefundsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof listRefunds>>>
export type ListRefundsInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listRefunds>>, ListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListRefundsParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData, QueryKey, ListRefundsParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRefunds>>,
          TError,
          Awaited<ReturnType<typeof listRefunds>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listRefunds>>, ListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData, QueryKey, ListRefundsParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRefunds>>,
          TError,
          Awaited<ReturnType<typeof listRefunds>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listRefunds>>, ListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData, QueryKey, ListRefundsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's refund requests, newest first
 */

export function useListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listRefunds>>, ListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData, QueryKey, ListRefundsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListRefundsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getListRefundsQueryOptions = <TData = Awaited<ReturnType<typeof listRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRefundsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRefunds>>> = ({ signal }) => listRefunds(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListRefundsQueryResult = NonNullable<Awaited<ReturnType<typeof listRefunds>>>
export type ListRefundsQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListRefunds<TData = Awaited<ReturnType<typeof listRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListRefundsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRefunds>>,
          TError,
          Awaited<ReturnType<typeof listRefunds>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRefunds<TData = Awaited<ReturnType<typeof listRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listRefunds>>,
          TError,
          Awaited<ReturnType<typeof listRefunds>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListRefunds<TData = Awaited<ReturnType<typeof listRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's refund requests, newest first
 */

export function useListRefunds<TData = Awaited<ReturnType<typeof listRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listRefunds>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListRefundsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getQuoteRefundUrl = (params?: QuoteRefundParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/refunds/quote?${stringifiedParams}` : `/refunds/quote`
}

/**
 * With `amount`, also what that amount would come to (`selected`).
 * @summary How much can be refunded now, and where to
 */
export const quoteRefund = async (params?: QuoteRefundParams, options?: Parameters<typeof apiFetch>[1]): Promise<RefundQuote> => {

  return apiFetch<RefundQuote>(getQuoteRefundUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getQuoteRefundQueryKey = (params?: QuoteRefundParams,) => {
    return [
    `/refunds/quote`, ...(params ? [params] : [])
    ] as const;
    }


export const getQuoteRefundQueryOptions = <TData = Awaited<ReturnType<typeof quoteRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: QuoteRefundParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof quoteRefund>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getQuoteRefundQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof quoteRefund>>> = ({ signal }) => quoteRefund(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof quoteRefund>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type QuoteRefundQueryResult = NonNullable<Awaited<ReturnType<typeof quoteRefund>>>
export type QuoteRefundQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useQuoteRefund<TData = Awaited<ReturnType<typeof quoteRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  QuoteRefundParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof quoteRefund>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof quoteRefund>>,
          TError,
          Awaited<ReturnType<typeof quoteRefund>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useQuoteRefund<TData = Awaited<ReturnType<typeof quoteRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: QuoteRefundParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof quoteRefund>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof quoteRefund>>,
          TError,
          Awaited<ReturnType<typeof quoteRefund>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useQuoteRefund<TData = Awaited<ReturnType<typeof quoteRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: QuoteRefundParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof quoteRefund>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary How much can be refunded now, and where to
 */

export function useQuoteRefund<TData = Awaited<ReturnType<typeof quoteRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: QuoteRefundParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof quoteRefund>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getQuoteRefundQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getCancelRefundUrl = (id: string,) => {




  return `/refunds/${id}/cancel`
}

/**
 * The credit goes back to the wallet.
 * @summary Withdraw an open refund request
 */
export const cancelRefund = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Refund> => {

  return apiFetch<Refund>(getCancelRefundUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelRefundMutationKey = () => ['cancelRefund'] as const;

export const getCancelRefundMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelRefund>>, TError,CancelRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelRefund>>, TError,CancelRefundMutationVariables, TContext> => {

const mutationKey = getCancelRefundMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelRefund>>, CancelRefundMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelRefund(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelRefundMutationResult = NonNullable<Awaited<ReturnType<typeof cancelRefund>>>

    export type CancelRefundMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse
    export type CancelRefundMutationVariables = {id: string}

    /**
 * @summary Withdraw an open refund request
 */
export const useCancelRefund = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelRefund>>, TError,CancelRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof cancelRefund>>,
        TError,
        CancelRefundMutationVariables,
        TContext
      > => {
      return useMutation(getCancelRefundMutationOptions(options), queryClient);
    }
    export const getAdminListRefundsUrl = (params?: AdminListRefundsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/refunds?${stringifiedParams}` : `/admin/refunds`
}

/**
 * @summary Refund requests
 */
export const adminListRefunds = async (params?: AdminListRefundsParams, options?: Parameters<typeof apiFetch>[1]): Promise<AdminRefundPage> => {

  return apiFetch<AdminRefundPage>(getAdminListRefundsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListRefundsInfiniteQueryKey = (params?: AdminListRefundsParams,) => {
    return [
    'infinite', `/admin/refunds`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListRefundsQueryKey = (params?: AdminListRefundsParams,) => {
    return [
    `/admin/refunds`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListRefundsInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListRefunds>>, AdminListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData, QueryKey, AdminListRefundsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListRefundsInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListRefunds>>, QueryKey, AdminListRefundsParams['cursor']> = ({ signal, pageParam }) => adminListRefunds({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData, QueryKey, AdminListRefundsParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListRefundsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListRefunds>>>
export type AdminListRefundsInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListRefunds>>, AdminListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListRefundsParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData, QueryKey, AdminListRefundsParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListRefunds>>,
          TError,
          Awaited<ReturnType<typeof adminListRefunds>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListRefunds>>, AdminListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData, QueryKey, AdminListRefundsParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListRefunds>>,
          TError,
          Awaited<ReturnType<typeof adminListRefunds>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListRefunds>>, AdminListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData, QueryKey, AdminListRefundsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Refund requests
 */

export function useAdminListRefundsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListRefunds>>, AdminListRefundsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListRefundsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData, QueryKey, AdminListRefundsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListRefundsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListRefundsQueryOptions = <TData = Awaited<ReturnType<typeof adminListRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListRefundsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListRefunds>>> = ({ signal }) => adminListRefunds(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListRefundsQueryResult = NonNullable<Awaited<ReturnType<typeof adminListRefunds>>>
export type AdminListRefundsQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListRefunds<TData = Awaited<ReturnType<typeof adminListRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListRefundsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListRefunds>>,
          TError,
          Awaited<ReturnType<typeof adminListRefunds>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListRefunds<TData = Awaited<ReturnType<typeof adminListRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListRefunds>>,
          TError,
          Awaited<ReturnType<typeof adminListRefunds>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListRefunds<TData = Awaited<ReturnType<typeof adminListRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Refund requests
 */

export function useAdminListRefunds<TData = Awaited<ReturnType<typeof adminListRefunds>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListRefundsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListRefunds>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListRefundsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminGetRefundUrl = (id: string,) => {




  return `/admin/refunds/${id}`
}

/**
 * @summary One refund with its destination and the lots it takes
 */
export const adminGetRefund = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<AdminRefund> => {

  return apiFetch<AdminRefund>(getAdminGetRefundUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetRefundQueryKey = (id: string,) => {
    return [
    `/admin/refunds/${id}`
    ] as const;
    }


export const getAdminGetRefundQueryOptions = <TData = Awaited<ReturnType<typeof adminGetRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRefund>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetRefundQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetRefund>>> = ({ signal }) => adminGetRefund(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetRefund>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetRefundQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetRefund>>>
export type AdminGetRefundQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetRefund<TData = Awaited<ReturnType<typeof adminGetRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRefund>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetRefund>>,
          TError,
          Awaited<ReturnType<typeof adminGetRefund>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetRefund<TData = Awaited<ReturnType<typeof adminGetRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRefund>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetRefund>>,
          TError,
          Awaited<ReturnType<typeof adminGetRefund>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetRefund<TData = Awaited<ReturnType<typeof adminGetRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRefund>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One refund with its destination and the lots it takes
 */

export function useAdminGetRefund<TData = Awaited<ReturnType<typeof adminGetRefund>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetRefund>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetRefundQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminApproveRefundUrl = (id: string,) => {




  return `/admin/refunds/${id}/approve`
}

/**
 * @summary Approve a refund request for payout
 */
export const adminApproveRefund = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<AdminRefund> => {

  return apiFetch<AdminRefund>(getAdminApproveRefundUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAdminApproveRefundMutationKey = () => ['adminApproveRefund'] as const;

export const getAdminApproveRefundMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminApproveRefund>>, TError,AdminApproveRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminApproveRefund>>, TError,AdminApproveRefundMutationVariables, TContext> => {

const mutationKey = getAdminApproveRefundMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminApproveRefund>>, AdminApproveRefundMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  adminApproveRefund(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminApproveRefundMutationResult = NonNullable<Awaited<ReturnType<typeof adminApproveRefund>>>

    export type AdminApproveRefundMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse
    export type AdminApproveRefundMutationVariables = {id: string}

    /**
 * @summary Approve a refund request for payout
 */
export const useAdminApproveRefund = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminApproveRefund>>, TError,AdminApproveRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminApproveRefund>>,
        TError,
        AdminApproveRefundMutationVariables,
        TContext
      > => {
      return useMutation(getAdminApproveRefundMutationOptions(options), queryClient);
    }
    export const getAdminRejectRefundUrl = (id: string,) => {




  return `/admin/refunds/${id}/reject`
}

/**
 * @summary Reject a refund request; the credit goes back to the wallet
 */
export const adminRejectRefund = async (id: string,
    reasonInput: ReasonInput, options?: Parameters<typeof apiFetch>[1]): Promise<AdminRefund> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<AdminRefund>(getAdminRejectRefundUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reasonInput)
  }
);}





export const getAdminRejectRefundMutationKey = () => ['adminRejectRefund'] as const;

export const getAdminRejectRefundMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminRejectRefund>>, TError,AdminRejectRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminRejectRefund>>, TError,AdminRejectRefundMutationVariables, TContext> => {

const mutationKey = getAdminRejectRefundMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminRejectRefund>>, AdminRejectRefundMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  adminRejectRefund(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminRejectRefundMutationResult = NonNullable<Awaited<ReturnType<typeof adminRejectRefund>>>
    export type AdminRejectRefundMutationBody = ReasonInput
    export type AdminRejectRefundMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminRejectRefundMutationVariables = {id: string;data: ReasonInput}

    /**
 * @summary Reject a refund request; the credit goes back to the wallet
 */
export const useAdminRejectRefund = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminRejectRefund>>, TError,AdminRejectRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminRejectRefund>>,
        TError,
        AdminRejectRefundMutationVariables,
        TContext
      > => {
      return useMutation(getAdminRejectRefundMutationOptions(options), queryClient);
    }
    export const getAdminPayoutRefundUrl = (id: string,) => {




  return `/admin/refunds/${id}/payout`
}

/**
 * Staff never pay out their own refund, nor one of a user they could not manage (403 `privilege_escalation`).
 * @summary Record an approved refund as paid, and issue its credit note
 */
export const adminPayoutRefund = async (id: string,
    adminPayoutRefundBody: AdminPayoutRefundBody, options?: Parameters<typeof apiFetch>[1]): Promise<AdminRefund> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<AdminRefund>(getAdminPayoutRefundUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminPayoutRefundBody)
  }
);}





export const getAdminPayoutRefundMutationKey = () => ['adminPayoutRefund'] as const;

export const getAdminPayoutRefundMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminPayoutRefund>>, TError,AdminPayoutRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminPayoutRefund>>, TError,AdminPayoutRefundMutationVariables, TContext> => {

const mutationKey = getAdminPayoutRefundMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminPayoutRefund>>, AdminPayoutRefundMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  adminPayoutRefund(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminPayoutRefundMutationResult = NonNullable<Awaited<ReturnType<typeof adminPayoutRefund>>>
    export type AdminPayoutRefundMutationBody = AdminPayoutRefundBody
    export type AdminPayoutRefundMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminPayoutRefundMutationVariables = {id: string;data: AdminPayoutRefundBody}

    /**
 * @summary Record an approved refund as paid, and issue its credit note
 */
export const useAdminPayoutRefund = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminPayoutRefund>>, TError,AdminPayoutRefundMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminPayoutRefund>>,
        TError,
        AdminPayoutRefundMutationVariables,
        TContext
      > => {
      return useMutation(getAdminPayoutRefundMutationOptions(options), queryClient);
    }
    export const getAdminGetCreditNotePdfUrl = (id: string,
    params?: AdminGetCreditNotePdfParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/credit-notes/${id}/pdf?${stringifiedParams}` : `/admin/credit-notes/${id}/pdf`
}

/**
 * `{id}` is the refund's id.
 * @summary A refund's credit note as a PDF, in the request's language
 */
export const adminGetCreditNotePdf = async (id: string,
    params?: AdminGetCreditNotePdfParams, options?: Parameters<typeof apiFetch>[1]): Promise<Blob> => {

  return apiFetch<Blob>(getAdminGetCreditNotePdfUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetCreditNotePdfQueryKey = (id: string,
    params?: AdminGetCreditNotePdfParams,) => {
    return [
    `/admin/credit-notes/${id}/pdf`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminGetCreditNotePdfQueryOptions = <TData = Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(id: string,
    params?: AdminGetCreditNotePdfParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetCreditNotePdfQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetCreditNotePdf>>> = ({ signal }) => adminGetCreditNotePdf(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetCreditNotePdfQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetCreditNotePdf>>>
export type AdminGetCreditNotePdfQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetCreditNotePdf<TData = Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string,
    params: undefined |  AdminGetCreditNotePdfParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetCreditNotePdf>>,
          TError,
          Awaited<ReturnType<typeof adminGetCreditNotePdf>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetCreditNotePdf<TData = Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string,
    params?: AdminGetCreditNotePdfParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetCreditNotePdf>>,
          TError,
          Awaited<ReturnType<typeof adminGetCreditNotePdf>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetCreditNotePdf<TData = Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string,
    params?: AdminGetCreditNotePdfParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary A refund's credit note as a PDF, in the request's language
 */

export function useAdminGetCreditNotePdf<TData = Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string,
    params?: AdminGetCreditNotePdfParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetCreditNotePdf>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetCreditNotePdfQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






